import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import { QueryClient, QueryClientProvider } from "react-query";
import { RequireAuth } from './RequireAuth';
import { Login } from './components/Login.jsx';
// import { ColorModeScript } from '@chakra-ui/react'
import { Routes, Route, Navigate } from 'react-router-dom';
import { ChakraProvider } from '@chakra-ui/react';
import { ContextWrapper } from './components/AppContext'
import { OAuthPopup } from "@tasoskakour/react-use-oauth2";
import './App.css'

import { PreviousLocationProvider } from './PreviousLocationContext.jsx';

import { RequireGroup } from './components/Auth/RequireGroup.jsx';
import { MainMain } from './components/MainMain'
import { PunchTimes } from './components/TimeAttendance/PunchTimes.jsx';
import { Dashboard } from './components/Dashboard/Dashboard.jsx';
import { TeamMembers } from './components/TeamMember/TeamMembers.jsx';
import { ViewApproveDJT } from './components/Billing/ViewApproveDJT.jsx';
import { SyncQBCustomer } from './components/Billing/SyncQBCustomer.jsx';
import { DailyJobTicket } from './components/DailyJobTicket.jsx'
import { Estimator } from './components/Estimator.jsx';
import { Units } from './components/Units/Units.jsx';
import { QueueDailyInspections } from './components/Inspections/QueueDailyInspections.jsx'
import { UnitInspections } from './components/Inspections/UnitInspections.jsx';
import { UserReceipts } from './components/ReceiptUpload/UserReceipts.jsx';
import { QuickCameraUploadStatement } from './components/ReceiptUpload/QuickCameraUploadStatement.jsx';
import { AccountsPayable } from './components/AccountsPayable/AccountsPayable.jsx';
import { ReceiptTransactionManagement } from './components/ReceiptTransactionManagement/ReceiptTransactionManagement.jsx';
import { ManageUnits } from './components/UnitsPrototype/ManageUnits.jsx';

import { UpdateBanner } from './components/UpdateBanner.jsx';

import { theme } from './theme/index.js';
import PropTypes from 'prop-types';

const queryClient = new QueryClient();

function MyRoutes() {
  const { route } = useAuthenticator((context) => [context.route]);
  // console.log('MyRoutes route: ', route);

  return (
    
    // <BrowserRouter>
      <PreviousLocationProvider>
        <LocationBasedRoutes route={route} />
      </PreviousLocationProvider>
    // </BrowserRouter>
  );
}

// This is a new functional component that handles the routes and can now use the useLocation hook properly
function LocationBasedRoutes({ route }) {

  const location = useLocation();
  const navigate = useNavigate();
  const [redirectPath, setRedirectPath] = useState(null);

  // Check if the current route is for 'receipt-upload' and if query param exists
  const isReceiptUpload = location.pathname.includes('/receipt-upload');
  const hasIdParam = location.search.includes('id=');

  useEffect(() => {
    // Save the path if the user is navigating to receipt-upload
    if (isReceiptUpload && hasIdParam && !redirectPath) {
      setRedirectPath(location.pathname + location.search);
      // console.log("Saving redirect path: ", location.pathname + location.search);
    }
  }, [location, isReceiptUpload, hasIdParam, redirectPath]);

  useEffect(() => {
    // After authentication, navigate back to the saved receipt-upload URL
    if (route === 'authenticated' && redirectPath) {
      // console.log("Redirecting back to: ", redirectPath);
      navigate(redirectPath);
    }
  }, [route, redirectPath, navigate]);


  return (
    <Routes>
      {/* Unauthenticated */}
      {route !== 'authenticated' ? (
        <>
          <Route path="/" element={<Login />} />
          <Route path="/login" element={<Login />} />
        </>
      ) : (
        <>
          {/* Receipt upload via SMS link — bypasses normal layout */}
          {isReceiptUpload && hasIdParam ? (
            <Route
              path="/receipt-upload"
              element={<RequireAuth><QuickCameraUploadStatement /></RequireAuth>}
            />
          ) : (
            <Route path="/" element={<RequireAuth><MainMain /></RequireAuth>}>

              {/* ── Open to all authenticated users (team_member baseline) ── */}
              <Route path="/dashboard" element={<Dashboard />} />

              {/* ── Unit inspections — team_member, supervisor, exec_admin, maintenance ── */}
              <Route
                path="/tm-unit-inspections"
                element={
                  <RequireGroup groups={['team_member', 'supervisor', 'exec_admin', 'maintenance']}>
                    <UnitInspections />
                  </RequireGroup>
                }
              />

              {/* ── Credit card holders — own receipts ── */}
              <Route
                path="/user-receipts"
                element={
                  <RequireGroup groups={['issued_credit_card', 'exec_admin']}>
                    <UserReceipts />
                  </RequireGroup>
                }
              />

              <Route
                path="/daily-job-ticket"
                element={
                  <RequireGroup groups={['supervisor', 'exec_admin']}>
                    <DailyJobTicket />
                  </RequireGroup>
                }
              />
              <Route
                path="/estimator"
                element={
                  <RequireGroup groups={['supervisor', 'exec_admin']}>
                    <Estimator />
                  </RequireGroup>
                }
              />
              <Route
                path="/transfers"
                element={
                  <RequireGroup groups={['supervisor', 'exec_admin']}>
                    {/* component placeholder — currently disabled */}
                    <></>
                  </RequireGroup>
                }
              />

              {/* ── Supervisor + human_resources + exec_admin ── */}
              <Route
                path="/daily-punches"
                element={
                  <RequireGroup groups={['supervisor', 'human_resources', 'exec_admin']}>
                    <PunchTimes />
                  </RequireGroup>
                }
              />
              <Route
                path="/credentials"
                element={
                  <RequireGroup groups={['supervisor', 'human_resources', 'exec_admin']}>
                    {/* component placeholder — currently disabled */}
                    <></>
                  </RequireGroup>
                }
              />

              {/* ── Supervisor + accounting + human_resources + exec_admin ── */}
              <Route
                path="/teammembers"
                element={
                  <RequireGroup groups={['supervisor', 'accounting', 'human_resources', 'exec_admin']}>
                    <TeamMembers />
                  </RequireGroup>
                }
              />

              {/* ── Units — supervisor + maintenance + exec_admin ── */}
              <Route
                path="/units"
                element={
                  <RequireGroup groups={['supervisor', 'maintenance', 'exec_admin']}>
                    <Units />
                  </RequireGroup>
                }
              />

              {/* ── Inspection queue — supervisor + maintenance + exec_admin ── */}
              <Route
                path="/daily-inspections"
                element={
                  <RequireGroup groups={['supervisor', 'maintenance', 'exec_admin']}>
                    <QueueDailyInspections />
                  </RequireGroup>
                }
              />

              {/* ── Accounting + exec_admin ── */}
              <Route
                path="/viewdjt"
                element={
                  <RequireGroup groups={['accounting', 'exec_admin']}>
                    <ViewApproveDJT />
                  </RequireGroup>
                }
              />
              <Route
                path="/syncqbcustomer"
                element={
                  <RequireGroup groups={['accounting', 'exec_admin']}>
                    <SyncQBCustomer />
                  </RequireGroup>
                }
              />
              <Route
                path="/accounts-payable"
                element={
                  <RequireGroup groups={['accounting', 'exec_admin']}>
                    <AccountsPayable />
                  </RequireGroup>
                }
              />

              {/* ── Receipt management — credit_card_manager + credit_card_receipt_manager + accounting + exec_admin ── */}
              <Route
                path="/cc-trans-management"
                element={
                  <RequireGroup groups={['credit_card_manager', 'credit_card_receipt_manager', 'accounting', 'exec_admin']}>
                    <ReceiptTransactionManagement />
                  </RequireGroup>
                }
              />

              {/* ── exec_admin only ── */}
              <Route
                path="/units-prototype"
                element={
                  <RequireGroup groups={['exec_admin']}>
                    <ManageUnits />
                  </RequireGroup>
                }
              />

              {/* ── Disabled routes — placeholder until built ── */}
              <Route path="/tasks"            element={<></>} />
              <Route path="/statements"       element={<></>} />
              <Route path="/team-members"     element={<></>} />
              <Route path="/contracts-and-bids" element={<></>} />
              <Route path="/divisions"        element={<></>} />
              <Route path="/careers"          element={<></>} />
              <Route path="/contacts"         element={<></>} />

              {/* ── Commented out — dead routes kept for reference ── */}
              {/* <Route index path="/layout" element={<Layout />} /> */}
              {/* <Route path="/manage-credit-cards" element={<Receipt />} /> */}
              {/* <Route path="/playground" element={<Receipt />} /> */}

            </Route>
          )}
        </>
      )}

      <Route path="/callback" element={<OAuthPopup />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

LocationBasedRoutes.propTypes = {
  route: PropTypes.string,
}

function App() {
  // useEffect(() => {
  //   if ("serviceWorker" in navigator) {
  //     navigator.serviceWorker.ready.then((registration) => {
  //       console.log("Service Worker Ready:", registration);

  //       registration.update(); // Explicitly check for updates
  //     });

  //     navigator.serviceWorker.addEventListener("controllerchange", () => {
  //       console.log("A new version is available. Refresh the page!");

  //       // Offer the user a choice instead of forcing a reload
  //       if (window.confirm("Spike Brain Update Available!\n\nDo you want to reload to see the latest updates?")) {
  //         window.location.reload();
  //       }
  //     });
  //   }
  // }, []);
  // Service-worker registration and update handling now live in main.jsx
  // (registerSW with registerType: 'prompt'). The UpdateBanner component below
  // listens for the resulting 'pwa:need-refresh' event and offers a Reload
  // button. The old per-render controllerchange/confirm() block was removed:
  // it re-ran on every render, relied on skipWaiting behavior we've turned off,
  // and conflicted with the registerSW flow.

  const EnvIntegrityWatcher = () => {
    if (import.meta.env.VITE_APP_ENV !== 'development') return null;
    const appEnv  = import.meta.env.VITE_APP_ENV;
    const qbEnv   = import.meta.env.VITE_QBO_ENV   || '';
    const s3      = import.meta.env.VITE_S3_STATEMENT_BUCKET || '';

    // Only runs in dev — returns nothing in production
    if (appEnv !== 'development') return null;

    const mismatches = [];

    if (qbEnv !== 'sandbox')
      mismatches.push(`🚨 QBO env is "${qbEnv}" — expected "sandbox" in dev`);
    if (!s3.includes('-dev'))
      mismatches.push(`🚨 S3 bucket is "${s3}" — expected dev bucket`);

    if (mismatches.length === 0) return null;

    return (
      <div style={{
        position:   'fixed',
        bottom:     '10px',
        right:      '10px',
        background: '#000',
        color:      '#fff',
        padding:    '12px 16px',
        borderRadius: '6px',
        zIndex:     9999,
        fontSize:   '11px',
        fontFamily: 'monospace',
        border:     '2px solid red',
        minWidth:   '300px',
        opacity:    0.95,
      }}>
        <div style={{ fontWeight: 'bold', color: 'red', marginBottom: '8px', fontSize: '13px' }}>
          ⚠️ ENV MISMATCH — DEV
        </div>
        <div style={{ color: '#aaa', marginBottom: '6px' }}>
          VITE_APP_ENV: {appEnv}
        </div>
        {mismatches.map((m, i) => (
          <div key={i} style={{ marginBottom: '4px' }}>{m}</div>
        ))}
      </div>
    );
  };

  // useEffect(() => {
  //   if ("serviceWorker" in navigator) {
  //     navigator.serviceWorker.ready.then(registration => {
  //       console.log("Service Worker Ready:", registration);
  //     });

  //     navigator.serviceWorker.addEventListener("controllerchange", () => {
  //       console.log("A new version is available. Refresh the page!");
  //       alert('Spike Brain Update Available!\n\nTo see the latest updates:\n1. Log out.\n2. Refresh your screen.\n3. Log back in.\n\nThat\'s it! Enjoy the new features!');
  //       window.location.reload();
  //     });
  //   }
  // }, []);

  
        
  return (
    // <PreviousLocationProvider>
      <QueryClientProvider client={queryClient}>
        <ChakraProvider theme={theme}>
        {/* <ColorModeScript initialColorMode={theme.config.initialColorMode} /> */}
          <Authenticator.Provider>
            <ContextWrapper>
              <UpdateBanner />
              <EnvIntegrityWatcher />
              <MyRoutes />
            </ContextWrapper>
          </Authenticator.Provider>
          {/* <ColorModeSwitch /> */}
        </ChakraProvider>
      </QueryClientProvider>
    // </PreviousLocationProvider>
  )
}

export default App